import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, LogOut, User } from "lucide-react";
import { waLink, LOGO } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const links = [
  { label: "Story", id: "story" },
  { label: "Collection", id: "collection" },
  { label: "Custom", id: "custom" },
  { label: "Occasions", id: "occasions" },
  { label: "Love", id: "reviews" },
  { label: "Enquire", id: "enquire" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user, login, logout } = useAuth();

  const firstName = user?.name ? user.name.split(" ")[0] : "";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
    else if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "glass border-b border-[#311B4D]" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-5 md:px-12">
        <button
          onClick={() => scrollTo("hero")}
          data-testid="nav-logo"
          className="flex items-center"
        >
          <img
            src={LOGO}
            alt="Elixz Chocolates"
            className="h-16 w-auto md:h-20"
          />
        </button>

        <div className="hidden items-center gap-9 md:flex">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollTo(l.id)}
              data-testid={`nav-${l.id}`}
              className="text-sm text-[#A894C2] transition-colors duration-300 hover:text-[#FDFBF7]"
            >
              {l.label}
            </button>
          ))}
          {user ? (
            <div className="flex items-center gap-3" data-testid="nav-user">
              <span className="flex items-center gap-1.5 text-sm text-[#EAD178]">
                <User size={15} /> {firstName}
              </span>
              <button
                onClick={logout}
                data-testid="nav-logout"
                className="text-[#A894C2] transition-colors hover:text-gold"
                aria-label="Sign out"
              >
                <LogOut size={17} />
              </button>
            </div>
          ) : (
            <button
              onClick={() => login("/")}
              data-testid="nav-signin"
              className="text-sm text-[#A894C2] transition-colors duration-300 hover:text-[#FDFBF7]"
            >
              Sign In
            </button>
          )}
          <a
            href={waLink("Hi Elixz! I'd love to order some chocolates.")}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-whatsapp"
            className="rounded-full bg-gold px-6 py-2.5 text-sm font-semibold text-[#0F0616] transition-transform duration-300 hover:scale-[1.04] active:scale-95"
          >
            Order on WhatsApp
          </a>
        </div>

        <button
          className="text-gold md:hidden"
          onClick={() => setOpen(!open)}
          data-testid="nav-menu-toggle"
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden glass border-t border-[#311B4D] md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {links.map((l) => (
                <button
                  key={l.id}
                  onClick={() => scrollTo(l.id)}
                  className="py-3 text-left font-serif text-xl text-[#FDFBF7]"
                >
                  {l.label}
                </button>
              ))}
              {user ? (
                <button
                  onClick={() => { logout(); setOpen(false); }}
                  className="py-3 text-left font-serif text-xl text-[#EAD178]"
                  data-testid="mobile-logout"
                >
                  Sign Out ({firstName})
                </button>
              ) : (
                <button
                  onClick={() => login("/")}
                  className="py-3 text-left font-serif text-xl text-[#FDFBF7]"
                  data-testid="mobile-signin"
                >
                  Sign In
                </button>
              )}
              <a
                href={waLink("Hi Elixz! I'd love to order some chocolates.")}
                target="_blank"
                rel="noreferrer"
                className="mt-2 rounded-full bg-gold px-6 py-3 text-center font-semibold text-[#0F0616]"
              >
                Order on WhatsApp
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
